// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { FormFieldStyle } from "@insite/mobius/FormField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListFiltersButton from "@rc/App/Components/ProductList/ProductListFiltersButton";
import { screen } from "@rc/XDL/Helpers";
import { color, xScreen } from "@rc/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const location = getLocation(state);
    return {
        pageSize: pagination?.pageSize,
        totalItemCount: pagination?.totalItemCount,
        location,
    };
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductListControlsContainerStyles {
    container?: InjectableCss;
    sortGridItem?: InjectableCss;
    viewSelectGridItem?: InjectableCss;
    paginationGridItem?: InjectableCss;
    countWrapper?: InjectableCss;
    viewModePaginationWrapper?: InjectableCss;

    // xng-change
    filtersButtonGridItem?: InjectableCss;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            border: 1px solid ${color("common.border")};
            padding: 0 14px;
            flex-wrap: wrap;

            ${screen("lte", "md")`
                padding: 5px 14px;
            `};

            ${screen("lte", "sm")`
                padding: 11px 14px;
            `};

            ${screen("lte", "xs")`
                flex-wrap: nowrap;
            `};

            > div {
                padding-right: 20px;
                &:last-child {
                    padding-right: 0;
                    flex-wrap: wrap;
                    ${xScreen("gt", "sm")} {
                        min-width: 480px;
                    }
                }
            }

            .bottom-p {
                border-top: 0 !important;
            }
        `,
    },
    countWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: center;
            flex: 1 1 auto;
            min-width: 180px;
            max-width: 185px;
            ${screen("lte", "sm")`
                min-width: 100px;
                max-width: 100px;
            `};
        `,
    },
    viewModePaginationWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex: 1 1 calc(100% - 180px);
            max-width: calc(100% - 185px);
            margin-right: -20px;

            > div {
                padding-right: 20px;
            }

            ${screen("lte", "md")`
                max-width: initial;
                flex: 1 1 calc(100% - 100px);
                max-width: calc(100% - 100px);
            `};
        `,
    },
    sortGridItem: {
        css: css`
            flex: 2 1 auto;
            width: auto;
            min-width: 170px;
            max-width: 190px;
            ${screen("lte", "xs")`
                max-width: 135px;
                min-width: initial;
            `};
            > div {
                ${screen("lte", "xs")`
                    label {
                        display: none;
                    }
                `};
            }
        `,
    },
    viewSelectGridItem: {
        css: css`
            &&& {
                flex: 1 1 auto;
                width: auto;
                min-width: 76px;
                padding-right: 0;
                max-width: 90px;
                ${screen("lte", "md")`
                    display: none;
                `}
            }
        `,
    },
    paginationGridItem: {
        css: css`
            width: auto;
            flex: 2 1 auto;
            &.page_size {
                min-width: 170px;
                max-width: 200px;
                padding-right: 0;
                margin-right: 15px;
                ${screen("lte", "sm")`
                    margin-right: 10px;
                    width: 180px;
                `}
                ${FormFieldStyle} {
                    width: 200px;
                }
            }
            &.pagination_steps {
                max-width: 190px;
                padding-right: 15px;
            }
            ${screen("lte", "xs")`
                display: none;
            `};

            ${screen("lte", "xs")`
                max-width: calc(100% - 135px);

                .page-size__show{
                    display: none;
                }
            `}

            > div > div > div {
                justify-content: flex-end;
                > span {
                    ${screen("lte", "md")`
                        justify-content: flex-start;
                    `}
                }
            }
        `,
    },

    filtersButtonGridItem: {
        css: css`
            display: none;
            @media (max-width: 1200px) {
                display: block;
            }
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: React.FC<Props> = ({ id, location, pageSize, totalItemCount }) => {
    if (!pageSize) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...styles.filtersButtonGridItem}>
                <ProductListFiltersButton />
            </StyledWrapper>
            <StyledWrapper {...styles.container} className={`listcontroller${id}`}>
                <StyledWrapper {...styles.countWrapper}>
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
                <StyledWrapper {...styles.viewModePaginationWrapper}>
                    <StyledWrapper {...styles.sortGridItem}>
                        <Zone contentId={id} zoneName="Content01" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.paginationGridItem} className="page_size">
                        <Zone contentId={id} zoneName="Content02" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.viewSelectGridItem}>
                        <Zone contentId={id} zoneName="Content03" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.paginationGridItem} className="pagination_steps">
                        <Zone contentId={id} zoneName="Content04" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
